import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import type { CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import { type SendMessageContextParams } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type ContentContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import type { ReadContentCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';

import { populateSendMessageContext } from './populateSendMessageContext';
import type { ChatStoreContainerProps } from './types';

/**
 * Build the request context that `sendMessageConfirmedTools` sends. Wraps the private
 * `populateSendMessageContext` and narrows its result to the wire-payload shape
 * (`SendMessageContextParams`) the service accepts. Exported (instead of exporting
 * `populateSendMessageContext` directly) so the return type stays simple under
 * `--isolatedDeclarations` and `sendMessage`'s own typing is unaffected. The send path
 * retains ownership of the `includeAIFeatureContext` guard.
 */
export const populateConfirmedToolsContext = async (
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'],
	isFirstPrompt: boolean,
	addCurrentPageAsContextUserPreference: boolean | undefined,
	contentContextValue: ContentContext,
	productKey: string | undefined,
	sendAnalyticsEvent: (payload: AnalyticsEventPayload) => void,
	isEmptyStagingArea?: boolean,
	creationContextParams?: CreationContextParams,
	additionalContextReceiverId?: ReadContentCommandProperties['receiverId'],
): Promise<Partial<SendMessageContextParams>> => {
	let effectiveCreationContextParams: CreationContextParams | undefined = creationContextParams;
	if (effectiveCreationContextParams === undefined && isEmptyStagingArea) {
		effectiveCreationContextParams = { experience: 'cwr' };
	}

	const context = await populateSendMessageContext(
		chatContextStoreActions,
		isEmptyStagingArea || isFirstPrompt,
		undefined,
		undefined,
		addCurrentPageAsContextUserPreference,
		contentContextValue,
		effectiveCreationContextParams,
		productKey,
		sendAnalyticsEvent,
		undefined,
		undefined,
		undefined,
		undefined,
		undefined,
		additionalContextReceiverId,
	);
	return {
		editorContext: context.editorContext,
		whiteboardContext: context.whiteboardContext,
		slidesContext: context.slidesContext,
		databaseContext: context.databaseContext,
		dashboardEditorContext: context.dashboardEditorContext,
		browserContext: context.browserContext,
		additionalContext: context.additionalContext,
		aiFeatureContext: context.aiFeatureContext,
		jiraWorkflowContext: context.jiraWorkflowContext,
		dashboardContext: context.dashboardContext,
		useCurrentBrowsingContext: context.useCurrentBrowsingContext,
	};
};
